import React, { useMemo } from "react";

import { CheckboxFilter } from "./CheckboxFilter";
import {
  ACCOUNT_LEVEL_LABEL,
  ACCOUNT_LEVEL_VALUE,
  MAX_ACTIVITY_HOLDING_FILTER_IDS,
} from "./constants";
import { ActivityHoldingFilterProps } from "./types";

const ActivityHoldingFilterComponent: React.FC<ActivityHoldingFilterProps> = ({
  holdingOptions,
  holdingIds,
  accountLevel,
  onChange,
  isCompact = false,
}) => {
  const selected = useMemo(
    () => [
      ...(accountLevel ? [ACCOUNT_LEVEL_VALUE] : []),
      ...holdingIds.map(String),
    ],
    [accountLevel, holdingIds],
  );
  const isAtHoldingCap = holdingIds.length >= MAX_ACTIVITY_HOLDING_FILTER_IDS;
  const options = useMemo(
    () => [
      { value: ACCOUNT_LEVEL_VALUE, label: ACCOUNT_LEVEL_LABEL },
      ...holdingOptions.map((option) => ({
        ...option,
        disabled: isAtHoldingCap && !selected.includes(option.value),
      })),
    ],
    [holdingOptions, isAtHoldingCap, selected],
  );

  const triggerLabelOf = (values: string[]) => {
    if (values.length === 0) {
      return "All holdings";
    }
    if (values.length > 1) {
      return `${values.length} selected`;
    }
    return (
      options.find((option) => option.value === values[0])?.label ??
      "1 selected"
    );
  };

  const handleChange = (values: string[]) => {
    onChange({
      holdingIds: values
        .filter((value) => value !== ACCOUNT_LEVEL_VALUE)
        .map(Number),
      accountLevel: values.includes(ACCOUNT_LEVEL_VALUE),
    });
  };

  return (
    <CheckboxFilter
      options={options}
      selected={selected}
      onChange={handleChange}
      groupLabel="Filter by holding"
      triggerLabelOf={triggerLabelOf}
      isCompact={isCompact}
      testId="activity-holding-filter"
    />
  );
};

export const ActivityHoldingFilter = React.memo(ActivityHoldingFilterComponent);
